import { useEffect, useMemo, useState } from "react"
import { ChevronsUpDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import {
  COUNTRY_CODES,
  filterCountryCodes,
  findCountryByCode,
  findCountryByIso,
  formatFullPhone,
  isValidPhone,
  maxLocalDigitsFor,
  parsePhone,
} from "@/lib/phone"

export {
  COUNTRY_CODES,
  filterCountryCodes,
  findCountryByCode,
  formatFullPhone,
  isValidPhone,
  parsePhone,
}

interface PhoneInputProps {
  countryCode: string
  localPhone: string
  onCountryCodeChange: (value: string) => void
  onLocalPhoneChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  id?: string
  required?: boolean
}

export function PhoneInput({
  countryCode,
  localPhone,
  onCountryCodeChange,
  onLocalPhoneChange,
  disabled = false,
  placeholder = "55 1234 5678",
  id = "phone",
  required = true,
}: PhoneInputProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [selectedIso, setSelectedIso] = useState(() => findCountryByCode(countryCode).iso)

  useEffect(() => {
    const current = findCountryByIso(selectedIso)
    if (current.code !== countryCode) {
      setSelectedIso(findCountryByCode(countryCode).iso)
    }
  }, [countryCode, selectedIso])

  const selected = findCountryByIso(selectedIso)
  const matches = useMemo(() => filterCountryCodes(query), [query])
  const localDigits = localPhone.replace(/\D/g, "")
  const maxLocalDigits = maxLocalDigitsFor(selected.code)

  return (
    <div className="flex gap-2">
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (disabled) return
          setOpen(next)
          if (!next) setQuery("")
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            aria-label={`Código de país ${selected.flag} ${selected.code}`}
            className="h-10 min-w-[7.5rem] shrink-0 justify-between px-3 font-normal"
          >
            <span>
              {selected.flag} {selected.code}
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          className="z-[90] w-80 max-h-[min(20rem,50vh)] overflow-hidden p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="border-b border-border p-2">
            <Input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar país o código"
              aria-label="Buscar país"
            />
          </div>
          <div className="max-h-[min(16rem,40vh)] overflow-y-auto p-1">
            {matches.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                Ningún país coincide.
              </p>
            ) : (
              matches.map((country) => {
                const isActive = country.iso === selected.iso
                return (
                  <button
                    key={country.iso}
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm",
                      "hover:bg-accent hover:text-accent-foreground",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      "active:bg-accent/80",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                      isActive && "bg-accent text-accent-foreground",
                    )}
                    onClick={() => {
                      setSelectedIso(country.iso)
                      onCountryCodeChange(country.code)
                      setOpen(false)
                      setQuery("")
                    }}
                  >
                    <span className="text-base">{country.flag}</span>
                    <span className="min-w-0 flex-1 truncate">{country.name}</span>
                    <span className="shrink-0 text-muted-foreground">{country.code}</span>
                  </button>
                )
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
      <Input
        id={id}
        type="tel"
        inputMode="numeric"
        placeholder={placeholder}
        value={localPhone}
        onChange={(e) => onLocalPhoneChange(e.target.value)}
        required={required}
        disabled={disabled}
        className="flex-1"
        title={`Ingresa el número local (${maxLocalDigits} dígitos o menos)`}
        minLength={4}
        maxLength={maxLocalDigits + 4}
        aria-invalid={localDigits.length > 0 && !isValidPhone(localPhone, selected.code)}
      />
    </div>
  )
}
